import { Box, CircularProgress } from '@mui/material';
import type { CtaTarget } from '../cta';
import { useLayoutStyles } from '../layouts/layout-context';
import type { CtaVariant } from '../layouts/types';
import { ArrowIcon } from './icons';

interface CtaButtonProps {
  target: CtaTarget;
  variant: CtaVariant;
  withArrow?: boolean;
}

const CtaIcon = ({ pending, withArrow }: { pending?: boolean; withArrow: boolean }) => {
  const s = useLayoutStyles('shared');
  // `1em` follows the button's font size, which steps down on mobile.
  if (pending) {
    return <CircularProgress size="1em" thickness={6} color="inherit" aria-hidden />;
  }
  return withArrow ? <ArrowIcon sx={s.buttonIcon} /> : null;
};

export const CtaButton = ({ target, variant, withArrow = false }: CtaButtonProps) => {
  const s = useLayoutStyles('shared');
  const content = (
    <>
      <span>{target.label}</span>
      <CtaIcon pending={target.pending} withArrow={withArrow} />
    </>
  );

  if (target.href) {
    return (
      <Box
        component="a"
        href={target.href}
        target="_blank"
        rel="noopener noreferrer"
        sx={s.button[variant]}
      >
        {content}
      </Box>
    );
  }

  // A busy button keeps `aria-disabled`, not `disabled`: a disabled button drops focus, so a
  // screen reader would never announce its busy state. One with nothing to act on drops out.
  return (
    <Box
      component="button"
      type="button"
      onClick={target.onClick}
      disabled={target.disabled}
      aria-busy={target.pending || undefined}
      aria-disabled={target.pending || undefined}
      sx={s.button[variant]}
    >
      {content}
    </Box>
  );
};

/** An in-page anchor dressed as a button, for "Compare memberships" and "View plans". */
export const AnchorButton = ({
  href,
  label,
  variant,
}: {
  href: string;
  label: string;
  variant: CtaVariant;
}) => {
  const s = useLayoutStyles('shared');

  return (
    <Box component="a" href={href} sx={s.button[variant]}>
      {label}
    </Box>
  );
};
